/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component } from '@angular/core';
import { type HttpResponse, type Polling, type Rest, HttpService, PollingService } from '@dm/core';
import { GlobalDataService } from 'app/common/services';
import { type System } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import QRCode from 'qrcode';

import { HelpMenuStrService } from '../help-menu-str.service';

@Component({
    selector: 'app-intelligent-customer-service',
    templateUrl: './intelligent-customer-service.component.html',
    styleUrls: ['./intelligent-customer-service.component.scss'],
})
export class intelligentCustomerServiceComponent implements OnInit, OnDestroy {
    /**
     * 二维码
     */
    qrOutput: string;
    /**
     * 是否显示智能客服弹窗
     */
    showIntelligentQa: boolean;
    /**
     * 首次上电时间
     */
    powerontime: number;

    polling$: Polling;
    loading = false;
    SWITCH = SWITCH;
    i18n = this.str.i18nLabel;

    /**
     * 智能客服跳转地址
     */
    qrcodeUrl = this.i18n.intelligentCustomerServiceWebSite;

    constructor(
        private globalData: GlobalDataService,
        private pollingService: PollingService,
        private http: HttpService,
        private str: HelpMenuStrService,
    ) { }

    ngOnInit(): void {
        this.showIntelligentQa = true;
        this.loading = true;
        this.startPolling();
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    /**
     * 生成二维码
     */
    async initQrCode(): Promise<any> {
        const systemInfo: System = this.globalData.getSystemInfo();
        const model: string = systemInfo.productModeString;
        const version: string = systemInfo?.SoftwarePatch ?? systemInfo?.pointRelease;
        const esn: string = systemInfo.ID;
        const currentTime: number = Date.now();
        // 接口未获取到正确的首次上电时间时，使用当前时间作为首次上电时间(参考的DME IQ)
        const powerontime: number = this.powerontime || currentTime;

        this.qrcodeUrl = `${this.i18n.intelligentCustomerServiceWebSite}e=${esn}&m=${model}&v=${version}&pt=${powerontime}&gt=${currentTime}`;

        this.qrOutput = await this.getQrImage(this.qrcodeUrl);
    }

    /**
     * 将链接转换成二维码image
     */
    async getQrImage(msg: string): Promise<any> {
        const qrImage = await QRCode.toString(msg, { type: 'svg' });
        return qrImage;
    }

    /**
     * 刷新二维码
     */
    refreshPolling(): void {
        this.loading = true;
        if (this.polling$) {
            this.stopPolling();
        }

        this.startPolling();
    }

    /**
     * 获取首次上电时间
     */
    getFirstPoweronTimeRest(): Rest {
        return this.http.makeUrl('system.getFirstPoweronTime');
    }

    /**
     * 轮询更新二维码（轮询间隔290s，参考其他二维码）
     */
    startPolling(): void {
        const rest = this.getFirstPoweronTimeRest();
        this.polling$ = this.pollingService.add(rest, 290);
        this.polling$.subscribe(
            (res: HttpResponse) => {
                const data = res.data || {};
                // 转换成毫秒
                this.powerontime = data.firstPoweronTime * 1000;

                this.initQrCode();
                this.loading = false;
            },
        );
        // 接口报错时，用当前时间替代来生成二维码（参考DME IQ）
        this.polling$.err.subscribe(
            () => {
                this.initQrCode();
                this.loading = false;
            },
        );
    }

    stopPolling(): void {
        if (!this.polling$) {
            return;
        }

        this.pollingService.remove(this.polling$);
    }

    close(): void {
        this.showIntelligentQa = false;
        this.stopPolling();
    }
}
